"use client";

import { useQuery } from "@tanstack/react-query";
import type { BlokProps } from "@trydig/storyblok";
import { useMemo } from "react";
import { rpc } from "#rpc";
import {
	useDealerFilters,
	useFilterDealers,
} from "~/components/bloks/DealerListingsBlok/useDealerListings";
import { Chevron } from "~/components/icons/Chevron";
import { Cross } from "~/components/icons/Cross";
import { SearchIcon } from "~/components/icons/SearchIcon";
import { TextField } from "~/components/ui/TextField";
import type { DealerListingsStoryblok } from "~/generated/sb/blok-types";
import type { BlokData } from "~/srpc/routes/story.router";
import styles from "./DealerListingsBlok.module.css";

export const DealerListingsBlok = ({
	data: initialData = [],
}: BlokProps<DealerListingsStoryblok, BlokData["dealerListings"]>) => {
	const { data } = useQuery({
		queryKey: ["dealers"],
		queryFn: async () => {
			return rpc.story.bloks.dealerListings();
		},
		refetchOnMount: true,
		initialData: initialData,
	});

	const dealers = useFilterDealers(data);
	return (
		<div data-layout="container">
			<DealerFilters dealers={data} />
			<div className={styles.listingViewport}>
				<DealersList dealers={dealers} />
			</div>
		</div>
	);
};

const DealerFilters = ({ dealers }: { dealers?: BlokData["dealerListings"] }) => {
	const { search, setSearch, city, setCity, country, setCountry } = useDealerFilters();

	const options = useMemo(() => {
		if (!dealers?.length)
			return {
				countries: [],
				cities: [],
			};

		const countries = new Set<string>();
		const cities = new Set<string>();

		dealers.forEach((dealer) => {
			dealer.content?.country && countries.add(dealer?.content?.country);
			dealer.content?.city && cities.add(dealer?.content?.city);
		});

		return {
			countries: Array.from(countries),
			cities: Array.from(cities),
		};
	}, [dealers]);

	return (
		<fieldset className={styles.filters}>
			<div className={styles.searchField}>
				<TextField
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					type="text"
					label="Søk etter forhandler"
					role="search"
					id="search"
					placeholder=" "
				/>
				<SearchIcon className={styles.searchIcon} />
				<button onClick={() => setSearch("")} className={styles.clearIcon}>
					<Cross size={24} />
				</button>
			</div>
			<div className={styles.selectGroup}>
				<div className={styles.selectField}>
					<select
						className={styles.select}
						name="country"
						id="country"
						value={country}
						onChange={(e) => setCountry(e.target.value)}
					>
						<option value="">Velg land</option>
						{options.countries.map((country) => (
							<option key={country} value={country}>
								{country}
							</option>
						))}
					</select>
					<div className={styles.indicator}>
						<Chevron direction="down" size="md" />
					</div>
				</div>
				<div className={styles.selectField}>
					<input
						className={styles.select}
						type="text"
						placeholder="Velg by"
						name="city"
						value={city}
						list="cities"
						onChange={(e) => setCity(e.target.value)}
					/>
					<div className={styles.indicator}>
						<Chevron direction="down" size="md" />
					</div>
					<datalist id="cities">
						<option value="">Velg by</option>
						{options.cities.map((city) => (
							<option key={city} value={city}>
								{city}
							</option>
						))}
					</datalist>
				</div>
			</div>
		</fieldset>
	);
};

const DealersList = ({ dealers }: { dealers: BlokData["dealerListings"] }) => {
	return (
		<>
			<table className={styles.dealerTable}>
				<thead>
					<tr>
						<td>Butikk</td>
						<td>Land</td>
						<td>By</td>
						<td>Addresse</td>
					</tr>
				</thead>
				<tbody>
					{dealers?.map((dealer) => (
						<tr key={dealer.uuid}>
							<td>{dealer.content?.name}</td>
							<td>{dealer.content?.country}</td>
							<td>{dealer.content?.city}</td>
							<td>
								<address className={styles.address}>
									<span>{dealer.content?.address}</span>
									<a
										href={`https://www.google.com/maps/search/?api=1&query=${dealer.content?.address}+${dealer.content?.city}+${dealer.content?.country}`}
										target="_blank"
										referrerPolicy="no-referrer"
										rel="noopener"
									>
										Se på kart
									</a>
								</address>
							</td>
						</tr>
					))}
				</tbody>
			</table>

			<ul className={styles.dealerList}>
				{dealers?.map((dealer) => (
					<li key={dealer.uuid}>
						<article>
							<h3 className={styles.dealerTitle}>{dealer.content?.name}</h3>
							<div className={styles.dealerLocation}>
								<p>{dealer.content?.country}</p>
								<p className={styles.city}>{dealer.content?.city}</p>
							</div>
							<address className={styles.address}>
								<span>{dealer.content?.address}</span>
								<a
									href={`https://www.google.com/maps/search/?api=1&query=${dealer.content?.name}+${dealer.content?.address}+${dealer.content?.city}+${dealer.content?.country}`}
									target="_blank"
									referrerPolicy="no-referrer"
									rel="noopener"
								>
									Se på kart
								</a>
							</address>
						</article>
					</li>
				))}
			</ul>
		</>
	);
};
